import { tz } from '@date-fns/tz'
import {
	recruiterAvailabilityShowQueryKey,
	useRecruiterAvailabilityShow,
	useRecruiterAvailabilityUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import type { FormState, Override, Slot } from './types'

const DEFAULT_SLOT_START = '09:00'
const DEFAULT_SLOT_END = '17:00'

const DEFAULT_FORM: FormState = {
	bufferMinutes: 15,
	maxBookingsPerDay: 8,
	minAdvanceNoticeHours: null,
	timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	schedule: {
		slots: [],
		overrides: [],
	},
}

function seedForm(data: unknown): FormState {
	if (data == null || typeof data !== 'object') {
		return DEFAULT_FORM
	}
	const d = data as FormState
	return {
		bufferMinutes: d.bufferMinutes ?? DEFAULT_FORM.bufferMinutes,
		maxBookingsPerDay: d.maxBookingsPerDay ?? DEFAULT_FORM.maxBookingsPerDay,
		minAdvanceNoticeHours: d.minAdvanceNoticeHours ?? null,
		timezone: d.timezone ?? DEFAULT_FORM.timezone,
		schedule: {
			slots: (d.schedule?.slots ?? []).map((s) => ({ ...s })),
			overrides: (d.schedule?.overrides ?? []).map((o) => ({
				from: o.from,
				to: o.to,
				open: o.open,
				start: o.start ?? null,
				end: o.end ?? null,
			})),
		},
	}
}

export interface UseAvailabilityFormReturn {
	form: FormState
	isLoading: boolean
	isLoadError: boolean
	isFirstTimeSetup: boolean
	isDirty: boolean
	savedRecently: boolean
	isPending: boolean
	validationError: string | null
	hasGenericError: boolean
	updateForm: (updater: (prev: FormState) => FormState) => void
	addSlot: (day: number) => void
	applyDefaultWorkingWeek: () => void
	removeSlot: (idx: number) => void
	updateSlot: (idx: number, field: keyof Slot, value: string | number) => void
	addOverride: () => void
	removeOverride: (idx: number) => void
	updateOverride: (idx: number, field: keyof Override, value: string | boolean | null) => void
	handleSave: () => void
}

export function useAvailabilityForm(): UseAvailabilityFormReturn {
	const queryClient = useQueryClient()
	const { data: serverData, isLoading, isError: isLoadError } = useRecruiterAvailabilityShow()
	const mutation = useRecruiterAvailabilityUpdate()

	const [form, setForm] = useState<FormState>(DEFAULT_FORM)
	const [isDirty, setIsDirty] = useState(false)
	const [savedRecently, setSavedRecently] = useState(false)
	const [hasStartedSetup, setHasStartedSetup] = useState(false)

	useEffect(() => {
		if (serverData != null && !isDirty) {
			setForm(seedForm(serverData))
		}
	}, [serverData, isDirty])

	function updateForm(updater: (prev: FormState) => FormState): void {
		setForm((prev) => updater(prev))
		setIsDirty(true)
		setSavedRecently(false)
		setHasStartedSetup(true)
	}

	function handleSave(): void {
		mutation.mutate(
			{ data: form },
			{
				onSuccess: (data) => {
					setForm(seedForm(data))
					void queryClient.invalidateQueries({ queryKey: recruiterAvailabilityShowQueryKey() })
					setIsDirty(false)
					setSavedRecently(true)
					setTimeout(() => setSavedRecently(false), 2500)
				},
			},
		)
	}

	function addSlot(day: number): void {
		updateForm((prev) => ({
			...prev,
			schedule: {
				...prev.schedule,
				slots: [...prev.schedule.slots, { day, start: DEFAULT_SLOT_START, end: DEFAULT_SLOT_END }],
			},
		}))
	}

	function applyDefaultWorkingWeek(): void {
		updateForm((prev) => ({
			...prev,
			schedule: {
				...prev.schedule,
				slots: [
					...prev.schedule.slots,
					...[1, 2, 3, 4, 5].map((day) => ({
						day,
						start: DEFAULT_SLOT_START,
						end: DEFAULT_SLOT_END,
					})),
				],
			},
		}))
	}

	function removeSlot(idx: number): void {
		updateForm((prev) => ({
			...prev,
			schedule: {
				...prev.schedule,
				slots: prev.schedule.slots.filter((_, i) => i !== idx),
			},
		}))
	}

	function updateSlot(idx: number, field: keyof Slot, value: string | number): void {
		updateForm((prev) => ({
			...prev,
			schedule: {
				...prev.schedule,
				slots: prev.schedule.slots.map((slot, i) => (i === idx ? { ...slot, [field]: value } : slot)),
			},
		}))
	}

	function addOverride(): void {
		const today = format(new Date(), 'yyyy-MM-dd', { in: tz(form.timezone) })
		updateForm((prev) => ({
			...prev,
			schedule: {
				...prev.schedule,
				overrides: [
					...prev.schedule.overrides,
					{ from: today, to: today, open: false, start: null, end: null },
				],
			},
		}))
	}

	function removeOverride(idx: number): void {
		updateForm((prev) => ({
			...prev,
			schedule: {
				...prev.schedule,
				overrides: prev.schedule.overrides.filter((_, i) => i !== idx),
			},
		}))
	}

	function updateOverride(idx: number, field: keyof Override, value: string | boolean | null): void {
		updateForm((prev) => ({
			...prev,
			schedule: {
				...prev.schedule,
				overrides: prev.schedule.overrides.map((override, i) =>
					i === idx ? { ...override, [field]: value } : override
				),
			},
		}))
	}

	// Empty-state is a first-visit affordance for never-configured recruiters, not a live
	// mirror of form emptiness. We derive it from the server's loaded state (was this recruiter
	// ever configured?) and latch it off once they engage — so clearing all rows mid-edit does
	// not bounce them back to the empty-state card.
	const seededServer = serverData != null ? seedForm(serverData) : null
	const isFirstTimeSetup = seededServer != null
		&& seededServer.schedule.slots.length === 0
		&& seededServer.schedule.overrides.length === 0
		&& !hasStartedSetup

	const validationErrors = mutation.error?.response?.data?.errors ?? {}
	const hasValidationErrors = Object.keys(validationErrors).length > 0
	const validationError = hasValidationErrors
		? (mutation.error?.response?.data?.message ?? null)
		: null
	const hasGenericError = mutation.isError && !hasValidationErrors

	return {
		form,
		isLoading,
		isLoadError,
		isFirstTimeSetup,
		isDirty,
		savedRecently,
		isPending: mutation.isPending,
		validationError,
		hasGenericError,
		updateForm,
		addSlot,
		applyDefaultWorkingWeek,
		removeSlot,
		updateSlot,
		addOverride,
		removeOverride,
		updateOverride,
		handleSave,
	}
}
